import "./AppHeader.css";
import { ArrowCircleIcon } from "../icons/Icons";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useI18n } from "../i18n";
import { asset } from "../config";

type AppHeaderProps = {
  /** Resident-facing crisis help (always available). */
  onGetHelp?: () => void;
  /** Open the "message your WorkWell coach" instructions popup. */
  onMessageCoach?: () => void;
  /** Languages this deployment serves; the switcher hides if fewer than two. */
  languages?: string[];
  currentLang?: string;
  onLanguageChange?: (lang: string) => void;
  languageBusy?: boolean;
};

/**
 * Header: NHS ICB mark + brand on the left, persistent actions tucked to the
 * right. The partner-logo strip renders separately (and scrolls with content).
 */
export function AppHeader({
  onGetHelp,
  onMessageCoach,
  languages = [],
  currentLang = "en",
  onLanguageChange,
  languageBusy = false,
}: AppHeaderProps) {
  const { t } = useI18n();
  return (
    <header className="app-header">
      <div className="app-header__lead">
        <img
          className="app-header__nhs"
          src={asset("logos/llr-nhs-logo.png")}
          alt="NHS Leicester, Leicestershire and Rutland Integrated Care Board"
        />
        <a
          className="app-header__brand"
          href="#main"
          aria-label={t("header.logoLabel")}
        >
          <span className="app-header__title">LLR Work and Health Support</span>
          <span className="app-header__subtitle">Supporting People To Work and Live Well</span>
        </a>
      </div>

      <div className="app-header__actions">
        {onLanguageChange && (
          <LanguageSwitcher
            languages={languages}
            current={currentLang}
            onChange={onLanguageChange}
            disabled={languageBusy}
          />
        )}
        <button
          type="button"
          className="app-header__talk"
          onClick={onMessageCoach}
        >
          {/* Two labels, one shown at a time by CSS (AppHeader.css). A
              landscape phone has ~144px of header left after the actions, and
              the full label alone is 239px — it pushed the title under the
              language switcher (iPhone SE / Galaxy S22 landscape). Shortening
              the label here is what buys the title its room back; "Get Urgent
              Help" is left full length because it is the safety-critical one.
              Both spans are always in the DOM so the accessible name never
              depends on which breakpoint is active. */}
          <span className="app-header__talk-full">{t("header.messageCoach")}</span>
          <span className="app-header__talk-short">{t("header.messageCoachShort")}</span>
          <ArrowCircleIcon />
        </button>

        <button type="button" className="app-header__help" onClick={onGetHelp}>
          <span>{t("header.urgentHelp")}</span>
          <ArrowCircleIcon />
        </button>

        {/* Mobile home for the accessibility trigger — AccessibilityPanel
            portals its button in here so it sits beside Get Urgent Help
            instead of floating over the conversation (PROJ-119). Empty and
            hidden on desktop, where the side tab is used. */}
        <span id="a11y-header-slot" className="app-header__a11y-slot" />
      </div>
    </header>
  );
}
